import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { PoolPlansTab, SharePlanType, SharePlanUnionType } from '../../types/pool-plans.types';
import { useAppQuery } from '../use-app-query.hook';

type SharePlans = {
  companyId: string;
  currentPage: number;
  poolId: string;
  selectedTab: PoolPlansTab;
  take: number;
  type?: SharePlanType;
};

export const useSharePlans = ({
  companyId,
  poolId,
  currentPage,
  selectedTab,
  take,
  type,
}: NullableFields<SharePlans, 'currentPage' | 'selectedTab' | 'take'>) => {
  const queryClient = useQueryClient();

  const { data: sharePlans, isLoading } = useAppQuery<{
    sharePlans: SharePlanUnionType[];
    totalPages: number;
  }>({
    queryKey: [QueryKey.GET_SHARE_PLANS, companyId, poolId, { selectedTab, currentPage }],
    queryFn: () =>
      ApiService.get(
        {
          endpoint: BackendRoute.COMPANIES,
          routePath: [companyId, 'pools', poolId, 'share-plans'],
        },
        {
          queryParams: {
            ...(selectedTab
              ? {
                  isTerminated: selectedTab === PoolPlansTab.TERMINATED,
                  take,
                  type,
                  skip: (currentPage >= 1 ? currentPage - 1 : 0) * take,
                }
              : {}),
          },
        },
      ),
    enabled: !!companyId && !!poolId,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SHARE_PLANS] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VESTING_GRAPH] });
  }, [queryClient]);

  return {
    invalidateQuery,
    ...sharePlans,
    sharePlans: sharePlans?.sharePlans || [],
    isLoading,
  };
};
